import { useState, useEffect, useCallback } from 'react';

/**
 * Scroll-reveal visibility for the marketing pages.
 *
 * Every page tags its animated blocks with `data-animate-id` and feeds the
 * returned `isVisible(id)` into <AnimateOnScroll>. This hook replaces four
 * near-identical copies of the same logic (HomePage, ProductPage, WordPage,
 * ExcelPage), two of which used IntersectionObserver and two a scroll sweep.
 *
 * It uses a scroll sweep rather than IntersectionObserver for one reason:
 * IntersectionObserver only fires when the intersection STATE changes, so a
 * section that travels from below the fold to above it inside a single frame
 * (anchor jump, restored scroll position, a fast flick, keyboard End) never
 * reports as intersecting and stays invisible until the reader happens to
 * scroll back through it. The sweep instead asks "has this block reached the
 * fold yet", which is also true for anything already scrolled past, so the
 * page can never strand a section at opacity 0.
 *
 * Revealing is one-way: ids are only ever added, so nothing re-animates when
 * the reader scrolls back up. The sweep is rAF-coalesced and reads a handful
 * of rects, so it stays cheap on a scroll.
 */
export function useScrollReveal() {
  const [visibleElements, setVisibleElements] = useState(() => new Set());

  useEffect(() => {
    // Runs straight off the scroll event rather than through
    // requestAnimationFrame: rAF is throttled (or never fires) whenever the
    // page is not actively painting, which would leave sections unrevealed.
    // The pass reads a dozen-odd rects and skips ids it already knows, so it
    // is cheap enough to run inline.
    const sweep = () => {
      const fold = window.innerHeight - 40;
      setVisibleElements((prev) => {
        let next = prev;
        document.querySelectorAll('[data-animate-id]').forEach((el) => {
          const id = el.dataset.animateId;
          if (!id || next.has(id)) return;
          // Reached the fold, or already scrolled past it.
          if (el.getBoundingClientRect().top < fold) {
            if (next === prev) next = new Set(prev);
            next.add(id);
          }
        });
        return next;
      });
    };

    // These pages scroll inside a container rather than the window, and a
    // scroll event neither bubbles nor reliably reaches a capture-phase
    // window listener, so bind every scrollable ancestor of the animated
    // blocks as well as the window itself.
    const scrollTargets = new Set([window]);
    document.querySelectorAll('[data-animate-id]').forEach((el) => {
      for (let node = el.parentElement; node; node = node.parentElement) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') scrollTargets.add(node);
      }
    });

    // Initial pass on mount, plus one after layout settles (late-mounting
    // sections, web fonts, and images all shift the fold).
    sweep();
    const settle = window.setTimeout(sweep, 250);

    scrollTargets.forEach((t) => t.addEventListener('scroll', sweep, { passive: true }));
    window.addEventListener('resize', sweep);
    return () => {
      window.clearTimeout(settle);
      scrollTargets.forEach((t) => t.removeEventListener('scroll', sweep));
      window.removeEventListener('resize', sweep);
    };
  }, []);

  return useCallback((id) => visibleElements.has(id), [visibleElements]);
}

export default useScrollReveal;
